import type { PlainDate } from "@bivak/domain";
import { useNavigate } from "@tanstack/react-router";
import { nl } from "../../copy/nl.js";
import type { DagItem } from "../../lib/beheerKalender.js";

const MONTH_FORMAT = new Intl.DateTimeFormat("nl-BE", { month: "long" });
const DOW = ["ma", "di", "wo", "do", "vr", "za", "zo"] as const;

/**
 * Same hue vocabulary as the StatusChip, but SATURATED: chips sit on white
 * cards, these cells sit on beige paper, and the chip pastels drowned in it
 * (user feedback 2026-08-19). Occupied days are paint; free days recede.
 */
const KLEUR: Record<string, string> = {
  option: "border-[#c98f1d] bg-[#e3a92c] text-[#3d2c05]",
  confirmed: "border-dennen-diep bg-dennen text-[#e9efe8]",
  stayed: "border-[#48708d] bg-[#5b87a5] text-[#eef4f8]",
  settled: "border-[#8f8d7c] bg-[#a3a18e] text-[#28271f]",
  closed: "border-[#8f8d7c] bg-[#a3a18e] text-[#28271f]",
};
const BLOKKADE = "hatch border-[#b3a887] text-[#6b675a]";
// A status this map does not know still shows a NEUTRAL triangle — a
// transparent one would make the departure vanish when a status is added.
const DRIEHOEK: Record<string, string> = {
  option: "bg-[#e3a92c]",
  confirmed: "bg-dennen",
  stayed: "bg-[#5b87a5]",
  settled: "bg-[#a3a18e]",
  closed: "bg-[#a3a18e]",
};

function tooltipRegel(item: DagItem): string {
  if (item.soort === "blokkade") {
    return item.reason ?? nl.beheer.kalenderScherm.gesloten;
  }
  const status = (nl.status as Record<string, string>)[item.status] ?? item.status;
  const moment =
    item.aankomst && item.vertrek
      ? ""
      : item.vertrek
        ? ` · ${nl.beheer.kalenderScherm.vertrekt}`
        : "";
  return `${item.naam} · ${status.toLowerCase()}${moment}`;
}

export function BeheerMaand({
  month,
  dagen,
  todayIso,
}: {
  month: PlainDate;
  dagen: Map<string, DagItem[]>;
  todayIso: string;
}) {
  const navigate = useNavigate();
  const eerste = month.with({ day: 1 });
  // Monday-first grid: Temporal's dayOfWeek is already ISO (ma = 1).
  const voorloop = eerste.dayOfWeek - 1;

  const cellen: (PlainDate | null)[] = Array.from({ length: voorloop }, () => null);
  for (let dag = 1; dag <= eerste.daysInMonth; dag++) cellen.push(eerste.with({ day: dag }));

  return (
    // A card: the grid itself must read as an object on the paper — free days
    // recede INSIDE it, but the calendar as a whole must not (user feedback).
    <div className="rounded-2xl border border-lijn bg-kaart p-4 shadow-[0_2px_12px_rgba(35,38,28,0.06)]">
      <h4 className="mb-2 font-bold font-display text-sm capitalize">
        {MONTH_FORMAT.format(new Date(month.year, month.month - 1, 1))} {month.year}
      </h4>
      <div className="grid grid-cols-7 gap-[3px]">
        {DOW.map((day, index) => (
          <span
            key={day}
            className={`pb-1 text-center font-mono text-[10px] uppercase tracking-wider ${
              index >= 4 ? "font-semibold text-dennen" : "text-mist"
            }`}
          >
            {day}
          </span>
        ))}
        {cellen.map((datum, index) => {
          if (!datum) {
            // biome-ignore lint/suspicious/noArrayIndexKey: leading blanks are position-only
            return <span key={`blank-${index}`} />;
          }
          const iso = datum.toString();
          const items = dagen.get(iso) ?? [];
          const boekingen = items.filter((item) => item.soort === "boeking");
          const blokkade = items.find((item) => item.soort === "blokkade");
          // The whole-day occupant: an arrival, a mid-stay day, or a day rental.
          const vol = boekingen.find((item) => item.aankomst || !item.vertrek);
          // A departure-only day is half theirs — a corner, not a full cell.
          const vertrekker = boekingen.find((item) => item.vertrek && !item.aankomst);
          const vandaag = iso === todayIso;

          // Free days RECEDE (transparent on the paper); occupied days are the
          // paint. On a shared wisseldag the LEAVER owns the morning (base +
          // top-left), the arriver the evening (bottom-right triangle) — two
          // back-to-back groups must never fuse into one visual block, even
          // when their statuses share a colour (hence the paper seam).
          const wissel = vol && vertrekker;
          const basis = wissel
            ? (KLEUR[vertrekker.status] ?? "border-lijn bg-kaart")
            : vol
              ? (KLEUR[vol.status] ?? "border-lijn bg-kaart")
              : blokkade
                ? BLOKKADE
                : // No weekend tint — same reason as the public calendar: a cell
                  // colour outside the legend reads as a mystery fifth status.
                  "text-mist border-lijn/70 bg-transparent";
          const verleden = iso < todayIso ? " opacity-55" : "";
          const ring = vandaag ? " ring-2 ring-rood ring-offset-1 ring-offset-papier" : "";

          const doel = vol ?? vertrekker;
          const inhoud = (
            <>
              <span className="relative z-10">{datum.day}</span>
              {!vol && vertrekker && (
                <span
                  className={`absolute inset-0 rounded-md [clip-path:polygon(0_0,62%_0,0_62%)] ${DRIEHOEK[vertrekker.status] ?? "bg-[#d6d3c4]"}`}
                />
              )}
              {wissel && vol && (
                <>
                  <span className="absolute inset-0 rounded-md bg-papier [clip-path:polygon(100%_100%,100%_28%,28%_100%)]" />
                  <span
                    className={`absolute inset-0 rounded-md [clip-path:polygon(100%_100%,100%_36%,36%_100%)] ${DRIEHOEK[vol.status] ?? "bg-[#d6d3c4]"}`}
                  />
                </>
              )}
              {items.length > 0 && (
                <span className="-top-7 -translate-x-1/2 pointer-events-none absolute left-1/2 z-20 hidden whitespace-nowrap rounded-md bg-inkt px-2 py-1 font-sans text-[11px] text-kaart group-hover:block group-focus-visible:block">
                  {items.map(tooltipRegel).join(" — ")}
                </span>
              )}
            </>
          );
          const klasse = `group relative aspect-[1/0.82] rounded-md border p-1 text-left font-mono text-[10.5px] ${basis}${verleden}${ring}`;

          if (doel && doel.soort === "boeking") {
            return (
              <button
                key={iso}
                type="button"
                onClick={() =>
                  navigate({ to: "/beheer/boekingen/$boekingId", params: { boekingId: doel.id } })
                }
                aria-label={`${datum.day}: ${items.map(tooltipRegel).join(", ")}`}
                className={`${klasse} cursor-pointer`}
              >
                {inhoud}
              </button>
            );
          }
          return (
            // biome-ignore lint/a11y/useAriaPropsSupportedByRole: img role carries the blackout reason for screen readers
            <span
              key={iso}
              role={blokkade ? "img" : undefined}
              className={klasse}
              aria-label={blokkade ? tooltipRegel(blokkade) : undefined}
            >
              {inhoud}
            </span>
          );
        })}
      </div>
    </div>
  );
}
